import { useState, useEffect } from 'react';
import { Modal, Button, Alert, useToast } from '@/shared/components/ui';
import { rolService } from '../api/rolService';
import { RolDetalleDto } from '@/core/sdk/modules/administracion/rolClient';
import { MENSAJES_ERROR, TEXTOS_UI } from '../constants';
import styles from './RolModals.module.css';

interface Props {
    isOpen: boolean;
    rolId: number;
    onClose: () => void;
    onSuccess: () => void;
}

export const DeleteConfirmModal = ({ isOpen, rolId, onClose, onSuccess }: Props) => {
    const [rol, setRol] = useState<RolDetalleDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const { showToast } = useToast();

    useEffect(() => {
        loadRol();
    }, [rolId]);

    const loadRol = async () => {
        try {
            setLoadingData(true);
            const data = await rolService.getById(rolId);
            setRol(data);
        } catch (error: any) {
            showToast({
                type: 'error',
                message: error.message || 'Error al cargar rol'
            });
            onClose();
        } finally {
            setLoadingData(false);
        }
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            await rolService.delete(rolId);

            showToast({
                type: 'success',
                message: MENSAJES_ERROR.ELIMINADO_EXITOSO
            });

            onSuccess();
        } catch (error: any) {
            const errorMessage = error.response?.data?.message ||
                error.message ||
                MENSAJES_ERROR.ERROR_ELIMINAR;

            showToast({
                type: 'error',
                message: errorMessage
            });

            console.error('[DeleteRol] Error:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Eliminar Rol"
            size="md"
        >
            {loadingData ? (
                <div className={styles.loadingContainer}>
                    <p>{TEXTOS_UI.CARGANDO_DATOS}</p>
                </div>
            ) : (
                <div className={styles.deleteContent}>
                    <p className={styles.deleteWarning}>
                        {TEXTOS_UI.CONFIRMAR_ELIMINACION_TITULO} <strong>{rol?.nombre}</strong>?
                    </p>

                    {rol && rol.cantidadUsuarios > 0 && (
                        <Alert type="warning">
                            <p>
                                <strong>{TEXTOS_UI.ADVERTENCIA_PREFIJO}</strong> {TEXTOS_UI.ADVERTENCIA_USUARIOS} <strong>{rol.cantidadUsuarios}</strong> {TEXTOS_UI.ADVERTENCIA_SUFIJO}
                            </p>
                            <p>
                                {TEXTOS_UI.NO_PUEDE_ELIMINAR}
                            </p>
                        </Alert>
                    )}

                    {rol && rol.cantidadUsuarios === 0 && (
                        <p className={styles.deleteNote}>
                            {TEXTOS_UI.NOTA_ELIMINACION}
                        </p>
                    )}

                    <div className={styles.actions}>
                        <Button
                            variant="secondary"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleDelete}
                            disabled={loading || (rol?.cantidadUsuarios || 0) > 0}
                            loading={loading}
                        >
                            {loading ? TEXTOS_UI.BOTON_ELIMINANDO : TEXTOS_UI.BOTON_ELIMINAR}
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
};
